/**
 * Onboarding Wow Moment — Batch 2 (UX Sprint 1).
 *
 * 5-screen modal shown on a student's first login. Captures:
 *   1. Their course — pulled live from /api/student/available-courses (no
 *      more hardcoded LET/NLE/CPALE presets — the admin's active catalog
 *      is the source of truth)
 *   2. Their exam date — drives FSRS scheduling instantly
 *   3. Daily study time — 15 / 30 / 45 / 60+ minutes/day
 *   4. Reveal — big countdown + auto-computed daily cap + minutes/day
 *   5. CTA — drop them straight into a 3-card mini Smart Review session
 *
 * Self-mounting: reads /student/onboarding-status, only renders if
 * `should_show=true`. Marks complete on finish or explicit skip.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { studentPride } from "@/lib/confetti";
import {
  GraduationCap, CalendarDays, Sparkles, Rocket, X, ChevronRight, Loader2, Clock,
} from "lucide-react";

// Minutes/day presets. `est_cards` is a rough "cards you can review at ~15
// seconds per card" estimate we show on the reveal step.
const MINUTES_PRESETS = [
  { minutes: 15, label: "15 min",  desc: "Light streak",      cards: 30 },
  { minutes: 30, label: "30 min",  desc: "Solid daily habit", cards: 50 },
  { minutes: 45, label: "45 min",  desc: "Serious prep",      cards: 80 },
  { minutes: 60, label: "60+ min", desc: "Board-mode focus",  cards: 100 },
];

export default function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);   // 0..4
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null); // full course row or "other"
  const [examDate, setExamDate] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  // Self-mount: check status on first render
  useEffect(() => {
    api.get("/student/onboarding-status")
      .then((r) => { if (r.data?.should_show) setOpen(true); })
      .catch(() => {});
  }, []);

  // Load real courses when the modal first opens (courses list changes
  // rarely so we cache in state for the modal's lifetime).
  useEffect(() => {
    if (!open || courses.length) return;
    api.get("/student/available-courses")
      .then((r) => setCourses(Array.isArray(r.data) ? r.data.slice(0, 6) : []))
      .catch(() => setCourses([]));
  }, [open, courses.length]);

  const daysToExam = examDate
    ? Math.max(0, Math.ceil((new Date(examDate) - new Date()) / 86400000))
    : null;
  const recommendedCap = daysToExam !== null
    ? (daysToExam <= 14 ? 80 : daysToExam <= 60 ? 50 : 40)
    : (MINUTES_PRESETS.find((m) => m.minutes === minutes)?.cards || 50);

  const complete = useCallback(async (nextRoute = null) => {
    setSaving(true);
    try {
      // Save preferences (best-effort — the study-prefs endpoint accepts
      // any subset of these fields).
      const prefsPayload = { daily_study_minutes: minutes };
      if (examDate) prefsPayload.target_exam_date = examDate;
      await api.patch("/me/study-prefs", prefsPayload).catch(() => {});
      // Enroll the student in their chosen course as a free trial. If they
      // picked "Other" (selectedCourse === null with step > 0), we skip.
      if (selectedCourse && selectedCourse.id) {
        await api.post("/student/enroll-temporary", {
          course_id: selectedCourse.id,
        }).catch(() => {});
      }
      await api.post("/student/onboarding-complete");
      studentPride();
      setOpen(false);
      if (nextRoute) {
        setTimeout(() => nav(nextRoute), 250);
      }
    } catch (e) {
      toast.error("Could not save — try again");
    } finally {
      setSaving(false);
    }
  }, [examDate, minutes, selectedCourse, nav]);

  const skip = async () => {
    try { await api.post("/student/onboarding-complete"); } catch { /* silent — close anyway */ }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && skip()}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 bg-gradient-to-br from-slate-50 to-white"
        data-testid="onboarding-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Progress + close */}
        <div className="flex items-center gap-2 px-5 pt-4">
          <Progress value={(step + 1) * 20} className="h-1 flex-1" />
          <button
            type="button"
            aria-label="Skip"
            onClick={skip}
            className="text-slate-400 hover:text-slate-700 transition-colors p-1"
            data-testid="onboarding-skip-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 0 && (
          <div className="p-6 pt-4 space-y-4" data-testid="onboarding-step-1">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 grid place-items-center mb-3">
                <GraduationCap className="w-7 h-7" />
              </div>
              <h2 className="font-display font-extrabold text-xl tracking-tight">
                Welcome to PrepMasters
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Pick the course you&apos;re studying for — we&apos;ll personalize everything.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 max-h-72 overflow-y-auto">
              {courses.length === 0 ? (
                <div className="col-span-2 text-center text-sm text-slate-400 py-6">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading courses…
                </div>
              ) : (
                courses.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setSelectedCourse(c); setStep(1); }}
                    className="group rounded-xl border-2 border-slate-200 hover:border-blue-600 active:scale-[0.97] p-3 text-left transition-all"
                    data-testid={`onboarding-course-${c.id}`}
                  >
                    <div className="h-8 w-8 rounded-md bg-indigo-100 text-indigo-700 grid place-items-center mb-1.5">
                      <GraduationCap className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <div className="font-bold text-sm leading-tight">{c.course_name}</div>
                    {c.description && (
                      <div className="text-[11px] text-slate-500 leading-tight mt-0.5 line-clamp-2">{c.description}</div>
                    )}
                  </button>
                ))
              )}
              <button
                type="button"
                onClick={() => { setSelectedCourse(null); setStep(1); }}
                className="col-span-2 group rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-500 active:scale-[0.97] p-3 text-center transition-all text-slate-600 hover:text-slate-900"
                data-testid="onboarding-course-other"
              >
                <div className="text-xs font-semibold">I&apos;ll pick a course later</div>
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="p-6 pt-4 space-y-4" data-testid="onboarding-step-2">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center mb-3">
                <CalendarDays className="w-7 h-7" />
              </div>
              <h2 className="font-display font-extrabold text-xl tracking-tight">
                When is your exam?
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                We&apos;ll schedule your reviews to peak the day before.
              </p>
            </div>
            <Input
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
              className="text-base h-12"
              data-testid="onboarding-exam-date-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 active:scale-[0.97]" onClick={() => setStep(0)}>Back</Button>
              <Button
                className="flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.97]"
                disabled={!examDate}
                onClick={() => setStep(2)}
                data-testid="onboarding-step-2-next"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <button
              type="button"
              onClick={() => { setExamDate(""); setStep(2); }}
              className="block mx-auto text-xs text-slate-400 hover:text-slate-700"
              data-testid="onboarding-skip-date"
            >
              I don&apos;t have a date yet — skip
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="p-6 pt-4 space-y-4" data-testid="onboarding-step-3">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-3">
                <Clock className="w-7 h-7" />
              </div>
              <h2 className="font-display font-extrabold text-xl tracking-tight">
                How much time each day?
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Pick a realistic pace. You can change this later in Profile.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              {MINUTES_PRESETS.map((p) => {
                const active = minutes === p.minutes;
                return (
                  <button
                    key={p.minutes}
                    type="button"
                    onClick={() => setMinutes(p.minutes)}
                    className={`rounded-xl border-2 p-4 text-left transition-all active:scale-[0.97] ${
                      active
                        ? "border-emerald-600 bg-emerald-50 shadow-sm"
                        : "border-slate-200 hover:border-emerald-400"
                    }`}
                    data-testid={`onboarding-minutes-${p.minutes}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className={`font-display font-extrabold text-lg ${active ? "text-emerald-700" : "text-slate-800"}`}>
                        {p.label}
                      </div>
                      {active && <div className="text-emerald-600 text-lg leading-none">✓</div>}
                    </div>
                    <div className="text-[11px] text-slate-500 leading-tight">{p.desc}</div>
                    <div className="text-[11px] text-slate-400 mt-1 tabular-nums">~{p.cards} cards</div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>Back</Button>
              <Button
                className="flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.97]"
                onClick={() => setStep(3)}
                data-testid="onboarding-step-3-next"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="p-6 pt-4 space-y-5 text-center" data-testid="onboarding-step-4">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
              <Sparkles className="w-7 h-7" />
            </div>
            {daysToExam !== null ? (
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Your countdown</div>
                <div className="font-display font-black text-7xl tabular-nums bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 bg-clip-text text-transparent leading-none my-2">
                  {daysToExam}
                </div>
                <div className="text-sm font-semibold text-slate-700">days to exam day</div>
              </div>
            ) : (
              <h2 className="font-display font-extrabold text-xl">Your study plan, ready</h2>
            )}
            <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2">
              {selectedCourse?.course_name && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Course</span>
                  <span className="font-bold text-slate-900 text-right truncate max-w-[60%]">{selectedCourse.course_name}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Daily study time</span>
                <span className="font-bold text-slate-900 tabular-nums">
                  {MINUTES_PRESETS.find((m) => m.minutes === minutes)?.label}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Recommended daily cap</span>
                <span className="font-bold text-slate-900 tabular-nums">{recommendedCap} cards</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">FSRS spaced repetition</span>
                <span className="font-bold text-emerald-700">✓ Personalized</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Exam-day-aware scheduling</span>
                <span className="font-bold text-emerald-700">{examDate ? "✓ Enabled" : "Set later"}</span>
              </div>
            </div>
            <Button
              className="w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.97]"
              onClick={() => setStep(4)}
              data-testid="onboarding-step-4-next"
            >
              Looks great — let&apos;s start
            </Button>
          </div>
        )}

        {step === 4 && (
          <div className="p-6 pt-4 space-y-4 text-center" data-testid="onboarding-step-5">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-rose-100 text-rose-700 grid place-items-center">
              <Rocket className="w-7 h-7" />
            </div>
            <h2 className="font-display font-extrabold text-xl tracking-tight">Ready to review?</h2>
            <p className="text-sm text-slate-500">
              Start your first Smart Review session — we&apos;ll show you 3 questions calibrated to your level.
              Takes ~2 minutes.
            </p>
            <div className="flex flex-col gap-2 pt-1">
              <Button
                className="h-11 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:opacity-90 text-white active:scale-[0.97]"
                disabled={saving}
                onClick={() => complete("/student/smart-review")}
                data-testid="onboarding-start-review-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start my first Smart Review →"}
              </Button>
              <Button
                variant="ghost"
                className="text-slate-500 hover:text-slate-700"
                disabled={saving}
                onClick={() => complete(null)}
                data-testid="onboarding-explore-btn"
              >
                I&apos;ll explore the dashboard first
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
